/**
 * MotionPromptExpandedModal — large, screen-sized popup that hosts the Config
 * tab's prompt area (References | Style row, boundary note, Freeform /
 * Structured view, error banner, CINEMATOGRAPHY PRESETS) so it is easier to
 * edit than inside the canvas-scaled bottom panel.
 *
 * The caller moves its single motion-prompt element in here (it is not
 * duplicated), so editors, click-away saves and preset insertion keep working
 * unchanged.
 *
 * Bespoke rather than `ui/Modal` because:
 *  - Escape must close only when nothing inside used it — block / note /
 *    timestamp editors and the freeform box all use Escape to discard an edit
 *    (and `preventDefault()` it); `ui/Modal` closes on every Escape.
 *  - the motion prompt is a fill layout, so the dialog needs a fixed height.
 * Chrome (backdrop, surface, animation, z-index) mirrors `ui/Modal`.
 */

import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useBlockingOverlay } from '../../../../hooks/canvasShortcutGuard'
import { createNadDebug } from '../../../../utils/debugLog'

const expandLog = createNadDebug('dw:motion-prompt-expanded')

interface MotionPromptExpandedModalProps {
  nodeId: string
  onClose: (reason: 'escape' | 'backdrop' | 'close-button') => void
  children: ReactNode
}

export function MotionPromptExpandedModal({
  nodeId,
  onClose,
  children,
}: MotionPromptExpandedModalProps) {
  const { t } = useTranslation('canvas')
  const dialogRef = useRef<HTMLDivElement>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  // Covers the canvas: suspend canvas shortcuts (Delete/Backspace would
  // otherwise delete the still-selected node underneath).
  useBlockingOverlay(true)

  useEffect(() => {
    expandLog({ event: 'motion-prompt-expanded-open', nodeId })
    // Focus the dialog so keyboard events target it rather than the canvas.
    dialogRef.current?.focus({ preventScroll: true })
    const handleKeyDown = (e: KeyboardEvent) => {
      // SHORTCUT_ID: canvas.closeModal — keep keyboardShortcuts.ts in sync (Keyboard Shortcuts modal).
      // An editor inside that used Escape to discard its edit marks it handled.
      if (e.key !== 'Escape' || e.defaultPrevented) return
      // A modal opened from inside (Style, reference selector) stacks above
      // this one; its Escape closes only that modal.
      const dialogs = document.querySelectorAll('[role="dialog"][aria-modal="true"]')
      if (dialogs[dialogs.length - 1] !== dialogRef.current) return
      onCloseRef.current('escape')
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      expandLog({ event: 'motion-prompt-expanded-close', nodeId })
    }
  }, [nodeId])

  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center p-6 bg-black/70 animate-fade-in"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCloseRef.current('backdrop')
      }}
      role="presentation"
      data-testid="motion-prompt-expanded-backdrop"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('videoGeneratorNode.motionPrompt')}
        tabIndex={-1}
        className="relative flex flex-col w-full max-w-[1400px] h-[88vh] bg-surface-1 rounded-lg border border-border shadow-2xl p-5 pt-11 focus:outline-none animate-scale-in"
        data-testid="motion-prompt-expanded-dialog"
      >
        <button
          type="button"
          onClick={() => onCloseRef.current('close-button')}
          className="absolute top-3 right-3 w-7 h-7 rounded-md flex items-center justify-center text-text-muted hover:text-foreground hover:bg-surface-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          title={t('videoGeneratorNode.collapseMotionPrompt')}
          aria-label={t('videoGeneratorNode.collapseMotionPrompt')}
          data-testid="motion-prompt-expanded-close"
        >
          <X className="w-4 h-4" aria-hidden />
        </button>
        {children}
      </div>
    </div>,
    document.body,
  )
}
